import {
  REPORT_WINDOW_PRESETS,
  type ReportWindowPreset,
  type ReportWindowSelection,
} from "../types";
import { localDayEndIso, localDayStartIso } from "./localDayBounds";

/**
 * Default window preset — last 7 days, mirroring Industry Insights'
 * `DEFAULT_INSIGHTS_RANGE_PRESET` (SD-10000).
 */
export const DEFAULT_REPORT_WINDOW_PRESET: ReportWindowPreset = "7d";

export const isReportWindowPreset = (
  value: string | undefined,
): value is ReportWindowPreset =>
  value !== undefined &&
  (REPORT_WINDOW_PRESETS as readonly string[]).includes(value);

export const parseReportWindowPreset = (
  value: string | undefined,
): ReportWindowPreset | undefined =>
  isReportWindowPreset(value) ? value : undefined;

/**
 * Report hub v2 paths (SD-11016). The `/reports/v2/...` routes return the v2
 * disposition taxonomy. Same preset/custom split.
 */
export const buildInAppReportV2Path = (
  companyId: string,
  preset: ReportWindowPreset,
): string => `/companies/${companyId}/reports/v2/${preset}`;

export const buildCustomReportV2Path = (companyId: string): string =>
  `/companies/${companyId}/reports/v2/custom`;

export const buildBookingWizardReportPath = (
  companyId: string,
  preset: ReportWindowPreset,
): string => `/companies/${companyId}/reports/booking-wizard/${preset}`;

export const buildCustomBookingWizardReportPath = (companyId: string): string =>
  `/companies/${companyId}/reports/booking-wizard/custom`;

export const buildWebchatReportPath = (
  companyId: string,
  preset: ReportWindowPreset,
): string => `/companies/${companyId}/reports/webchat/${preset}`;

export const buildCustomWebchatReportPath = (companyId: string): string =>
  `/companies/${companyId}/reports/webchat/custom`;

/**
 * Booking Wizard session drill-down paths (SD-11593). Lists the sessions behind a
 * Booking Wizard report metric card over the same window the card was rendered
 * for. `custom` occupies the same preset path segment as the rolling windows and
 * takes inclusive local `start`/`end` dates (see `buildCustomWindowParams`).
 */
export const buildBookingWizardSessionsPath = (
  companyId: string,
  preset: ReportWindowPreset,
): string => `${buildBookingWizardReportPath(companyId, preset)}/sessions`;

export const buildCustomBookingWizardSessionsPath = (
  companyId: string,
): string => `${buildCustomBookingWizardReportPath(companyId)}/sessions`;

/**
 * Company-wide Campaigns dashboard paths (SD-11585 report, SD-11588 overview,
 * SD-11589 export). Same preset/custom split as the other Report hub endpoints:
 * `custom` is a real path segment and the window rides as `start`/`end` query
 * params (see `buildCustomWindowParams`). The export path takes the window as
 * query params on a single route (`preset` + optional `start`/`end` + `format`).
 */
export const buildCampaignReportPath = (
  companyId: string,
  preset: ReportWindowPreset,
): string => `/companies/${companyId}/reports/campaigns/${preset}`;

export const buildCustomCampaignReportPath = (companyId: string): string =>
  `/companies/${companyId}/reports/campaigns/custom`;

export const buildCampaignOverviewPath = (
  companyId: string,
  preset: ReportWindowPreset,
): string => `/companies/${companyId}/reports/campaigns/${preset}/overview`;

export const buildCustomCampaignOverviewPath = (companyId: string): string =>
  `/companies/${companyId}/reports/campaigns/custom/overview`;

export const buildCampaignReportExportPath = (companyId: string): string =>
  `/companies/${companyId}/reports/campaigns/export`;

/**
 * Per-call classification breakdown export (SD-12914; dale PR #6970). Single
 * route, window as query params (`preset` + optional `start`/`end`), plus
 * `channel` and `format`. The path company is always included server-side — the
 * repeatable `companyId` param only *adds* companies, so a single-company export
 * omits it entirely.
 */
export const buildCallBreakdownExportPath = (companyId: string): string =>
  `/companies/${companyId}/reports/calls/export`;

/** v2 account-wide aggregate paths (SD-11016) — the `/reports/v2` account counterparts. */
export const buildAccountReportV2Path = (
  accountId: string,
  preset: ReportWindowPreset,
): string => `/accounts/${accountId}/reports/v2/${preset}`;

export const buildCustomAccountReportV2Path = (accountId: string): string =>
  `/accounts/${accountId}/reports/v2/custom`;

export interface CustomReportWindowParams {
  start: string;
  end: string;
}

/**
 * Query params for the custom-range report. The backend (`router_reports.py`)
 * types `start` / `end` as **inclusive local dates** (`YYYY-MM-DD`), resolves the
 * window in the company timezone, and caps the span at ~92 days — so the picker
 * passes the calendar dates the user selected, not datetimes.
 */
export const buildCustomWindowParams = (
  start: string,
  end: string,
): CustomReportWindowParams => ({ start, end });

/**
 * Window query params shared by the company-local Report hub endpoints (metric
 * drill-down and caller-mix): the `preset` and, for custom, the inclusive local
 * `start`/`end` dates. Each backend re-resolves this to the same UTC window the
 * report card was rendered for. `custom` is sent as the literal preset value.
 */
export const buildReportWindowParams = (
  selection: ReportWindowSelection,
): { preset: string; start?: string; end?: string } =>
  selection.mode === "custom"
    ? { preset: "custom", start: selection.start, end: selection.end }
    : { preset: selection.preset };

/** Stable cache discriminator for a window selection (query-key fragment). */
export const reportWindowKey = (selection: ReportWindowSelection): string =>
  selection.mode === "custom"
    ? `custom:${selection.start}:${selection.end}`
    : selection.preset;

/** Trailing day-count for each rolling preset window. */
const PRESET_TRAILING_DAYS = {
  "1d": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
} satisfies Record<ReportWindowPreset, number>;

/**
 * Resolve a window selection to concrete `startDate`/`endDate` ISO strings for the
 * (date-only) conversations list endpoint, so a drill-down list covers the SAME
 * window the page shows instead of all-time. Custom ranges frame their inclusive
 * calendar dates in the USER'S timezone via `localDayStartIso`/`localDayEndIso` —
 * matching the overview tiles' own `statsDateRange` so a tile's drill-down modal
 * covers the same instants the tile was computed for. Presets are a trailing N-day
 * window ending now. This is the legacy stats-list path — the report-hub endpoints
 * frame presets server-side, but the conversations list only takes explicit dates.
 */
export const reportWindowToDateRange = (selection: ReportWindowSelection) => {
  if (selection.mode === "custom") {
    return {
      startDate: localDayStartIso(selection.start),
      endDate: localDayEndIso(selection.end),
    };
  }
  const end = new Date();
  const start = new Date(
    end.getTime() -
      PRESET_TRAILING_DAYS[selection.preset] * 24 * 60 * 60 * 1000,
  );
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};
